import { lazy, Suspense } from "react";
import {Switch, Route, Redirect} from "react-router-dom";
import {useSelector} from "react-redux";
import {getAutoCurrentChange, getAutoRequestType, getCurrentRequestForm} from "./selectors/requestAChangeSelector";

// Home and ScrollToTop stay eagerly imported: Home is the most-visited landing
// page, so keeping it in the main bundle lets it paint immediately with no
// Suspense flash. Every OTHER route is code-split with React.lazy so its JS only
// downloads when that route is actually visited, which keeps the initial
// homepage payload small (the fix for the slow First Contentful Paint on mobile).
import Home from "./views/Home";
import ScrollToTop from './utils/scrollToTop';

// --- Lazily-loaded routes (default exports) ---
const CommercialAutoInsurance = lazy(() => import("./views/CommercialAutoInsurance"));
const TruckingInsurance = lazy(() => import("./views/TruckingInsurance"));
const TruckingQuoteFormShort = lazy(() => import("./views/TruckingQuoteFormShort"));
const BusinessInsurance = lazy(() => import("./views/BusinessInsurance"));
const BusinessInsuranceForm = lazy(() => import("./views/BusinessInsuranceForm"));
const WorkersCompensation = lazy(() => import("./views/WorkersCompensation"));
const EarthquakeInsurance = lazy(() => import("./views/EarthquakeInsurance"));
const EarthquakeQuoteForm = lazy(() => import("./views/EarthquakeQuoteForm"));
const WorkersCompensationForm = lazy(() => import("./views/WorkersCompensationForm"));
const SR22Insurance = lazy(() => import("./views/SR-22-InsuranceQuote"));
const ApartmentRenterInsurance = lazy(() => import("./views/ApartmentRentersInsurance"));
const HomeOwnerInsurancePage = lazy(() => import("./views/HomeOwnerInsurance"));
const MotorcycleInsurancePage = lazy(() => import("./views/MotorcycleInsurance"));
const AutoInsuranceQuotePage = lazy(() => import("./views/AutoInsuranceQuote"));
const NonOwnerInsurancePage = lazy(() => import("./views/NonOwnerInsurance"));
const NonOwnerInsuranceForm = lazy(() => import("./views/NonOwnerInsuranceForm"));
const MakePaymentOnlinePage = lazy(() => import("./views/Claims"));
const RoadsideAssistancePage = lazy(() => import("./views/RoadsideAssitance"));
const OurHonestyPage = lazy(() => import("./views/OurHonesty"));
const DuiInsurancePage = lazy(() => import("./views/DUI-Insurance"));
const CareerPage = lazy(() => import("./views/Career"));
const PrivacyPolicyPage = lazy(() => import("./views/PrivacyPolicy"));
const OurMissionPage = lazy(() => import("./views/OurMission"));
const ContactUsForm = lazy(() => import("./views/ContactUs"));
const RentersInsuranceForm = lazy(() => import("./views/RenterInsuranceForm"));
const HomeInsuranceForm = lazy(() => import("./views/HomeOwnerInsuranceForm"));
const SR22InsuranceForm = lazy(() => import("./views/SR-22-InsuranceForm"));
const MotorcycleInsuranceForm = lazy(() => import("./views/MotorCycleInsuranceForm"));
const AutoInsuranceForm = lazy(() => import("./views/AutoInsuranceForm"));
const ChangeRequestVehicle = lazy(() => import("./views/ChangeRequestVehicle"));
const DuiInsuranceForm = lazy(() => import("./views/DUI-InsuranceForm"));
const CareerForm = lazy(() => import("./views/CareerForm"));
const CommercialAutoInsuranceForm = lazy(() => import("./views/CommericalInsuranceForm"));
const MakePayment = lazy(() => import("./views/MakeAPayment"));
const AddressChangeRequest = lazy(() => import("./components/RequestAChange/AutoRequest/AddressChange"));
const AutoInsuranceInNorthHollywood = lazy(() => import("./views/AutoInsuranceNorthHollywood"));

// --- Lazily-loaded routes (named exports -> remap to `default` for React.lazy) ---
const WebsiteAccessibilityPage = lazy(() => import("./views/Website-Accesibility").then(m => ({ default: m.WebsiteAccessibilityPage })));
const RequestAChange = lazy(() => import("./components/RequestAChange/ComponentChanger/StepChnager").then(m => ({ default: m.RequestAChange })));
const DriverChange = lazy(() => import("./components/RequestAChange/AutoRequest/DriverChange").then(m => ({ default: m.DriverChange })));
const DriverRequestCurrentForm = lazy(() => import("./components/RequestAChange/AutoRequest/DriverChange/DriverCurrentFormChange").then(m => ({ default: m.DriverRequestCurrentForm })));
const LienholderChangeRequest = lazy(() => import("./components/RequestAChange/AutoRequest/LineholderChange").then(m => ({ default: m.LienholderChangeRequest })));
const OtherChangeRequest = lazy(() => import("./components/RequestAChange/AutoRequest/OtherChange").then(m => ({ default: m.OtherChangeRequest })));
const DocumentRequest = lazy(() => import("./components/RequestAChange/AutoRequest/DocumentRequest").then(m => ({ default: m.DocumentRequest })));
const VehicleRequest = lazy(() => import("./components/RequestAChange/AutoRequest/VehicleRequest").then(m => ({ default: m.VehicleRequest })));
const InsuranceChangeRequest = lazy(() => import("./components/RequestAChange/Commercial-Home-Other").then(m => ({ default: m.InsuranceChangeRequest })));
const Remove_Replace_Driver = lazy(() => import("./components/RequestAChange/AutoRequest/DriverChange/Remove-Replace").then(m => ({ default: m.Remove_Replace_Driver })));
const RequestChangeThankYouPage = lazy(() => import("./components/RequestAChange/thank-you-page-request").then(m => ({ default: m.RequestChangeThankYouPage })));
const AddVehicleRequestChange = lazy(() => import("./components/RequestAChange/AutoRequest/VehicleRequest/Add").then(m => ({ default: m.AddVehicleRequestChange })));
const VehicleRemoveReplace = lazy(() => import("./components/RequestAChange/AutoRequest/VehicleRequest/Remove-Replace").then(m => ({ default: m.VehicleRemoveReplace })));

// Shown only while a route's split chunk is downloading. Reserves vertical space
// so there's no layout shift, and uses the brand-blue spinner from style.css.
const RouteFallback = () => (
  <div className="route-suspense-loader" aria-label="Loading" role="status">
    <div className="route-suspense-spinner" />
  </div>
);

export default function Routes() {
  const autoDetectCurrent = useSelector(getAutoCurrentChange)
  const autoDetectCurrentType = useSelector(getAutoRequestType)
  const currentForm = useSelector(getCurrentRequestForm)

  return (
    <>
    <ScrollToTop />
    <Suspense fallback={<RouteFallback />}>
    <Switch>
      <Route exact path="/commercial-auto-insurance">
        <CommercialAutoInsurance />
      </Route>
      <Route exact path="/trucking-insurance">
        <TruckingInsurance />
      </Route>
      {/* Official trucking quote form lives at /trucking-insurance-quote.
          The retired "-short" path redirects here so old links funnel to the
          canonical URL. The redirect route is listed first because the route
          below is non-exact and would otherwise prefix-match "-short". */}
      <Route path="/trucking-insurance-quote-short">
        <Redirect to="/trucking-insurance-quote" />
      </Route>
      <Route path="/trucking-insurance-quote">
        <TruckingQuoteFormShort />
      </Route>
      <Route exact path="/business-insurance">
        <BusinessInsurance />
      </Route>
      <Route path="/business-insurance-quote">
        <BusinessInsuranceForm />
      </Route>
      <Route exact path="/workers-compensation-insurance">
        <WorkersCompensation />
      </Route>
      <Route path="/workers-compensation-form">
        <WorkersCompensationForm />
      </Route>
      <Route exact path="/earthquake-insurance">
        <EarthquakeInsurance />
      </Route>
      <Route path="/earthquake-insurance-quote">
        <EarthquakeQuoteForm />
      </Route>
      <Route path="/commercial-auto-insurance-form">
        <CommercialAutoInsuranceForm />
      </Route>
      <Route exact path="/contact-us">
          <ContactUsForm />
      </Route>
      <Route exact path="/sr-22-insurance">
        <SR22Insurance />
      </Route>
      <Route exact path="/apartment-renter-insurance">
        <ApartmentRenterInsurance />
      </Route>
      <Route exact path="/motorcycle-insurance">
        <MotorcycleInsurancePage />
      </Route>
      <Route exact path="/home-owner-insurance">
        <HomeOwnerInsurancePage />
      </Route>
      <Route path="/home-owner-insurance-form">
        <HomeInsuranceForm />
      </Route>
      <Route exact path="/auto-insurance-quote">
        <AutoInsuranceQuotePage />
      </Route>
      <Route exact path="/non-owner-insurance">
        <NonOwnerInsurancePage />
      </Route>
      <Route path="/non-owner-insurance-form">
        <NonOwnerInsuranceForm />
      </Route>
      <Route exact path="/make-payment-online">
        <MakePayment />
      </Route>
      <Route exact path="/roadside-assistance">
        <RoadsideAssistancePage />
      </Route>
      <Route exact path="/customer-reviews">
        <OurHonestyPage />
      </Route>
      <Route exact path="/dui-insurance">
        <DuiInsurancePage />
      </Route>
      <Route exact path="/career">
        <CareerPage />
      </Route>
      <Route  path="/apply">
        <CareerForm />
      </Route>
      <Route exact path="/privacy-policy">
        <PrivacyPolicyPage />
      </Route>
      <Route exact path="/about-us">
        <OurMissionPage />
      </Route>
      <Route path="/renters-insurance-form">
        <RentersInsuranceForm />
      </Route>
      <Route path="/sr22-insurance-form">
        <SR22InsuranceForm />
      </Route>
      <Route  path="/motorcycle-insurance-form">
        <MotorcycleInsuranceForm />
      </Route>
      <Route path="/auto-insurance-form">
        <AutoInsuranceForm />
      </Route>
      <Route exact path="/change-request-vehicle-form">
        <ChangeRequestVehicle />
      </Route>
      <Route path="/dui-insurance-form">
        <DuiInsuranceForm />
      </Route>
      <Route path="/claims">
        <MakePaymentOnlinePage />
      </Route>
      <Route path="/autoinsurancenorthhollywood">
        <AutoInsuranceInNorthHollywood />
      </Route>
      <Route exact path="/">
        <Home />
      </Route>
      <Route exact path="/website-accessibility">
        <WebsiteAccessibilityPage />
      </Route>
      <Route exact path="/request-change">
        <RequestAChange/>
      </Route>
      <Route exact path="/address-request">
        {autoDetectCurrentType ? <AddressChangeRequest/> : <Redirect to="/" />}
      </Route>
      <Route exact path="/driver-change">
        <DriverChange/>
      </Route>
      <Route exact path="/request-change-thank-you">
        <RequestChangeThankYouPage/>
      </Route>
      <Route exact path="/driver-change-request">
        {autoDetectCurrent ? <DriverRequestCurrentForm/> : <Redirect to="/" />}
      </Route>
      <Route exact path="/lienholder-change">
        {autoDetectCurrentType ? <LienholderChangeRequest/> : <Redirect to="/" />}
      </Route>
      <Route exact path="/other-change">
        {autoDetectCurrentType ? <OtherChangeRequest/> : <Redirect to="/" />}
      </Route>
      <Route exact path="/policy-document-request">
        {autoDetectCurrentType ? <DocumentRequest/> : <Redirect to="/" />}
      </Route>
      <Route exact path="/vehicle-request">
        {autoDetectCurrentType ? <VehicleRequest/> : <Redirect to="/" />}
      </Route>
      <Route exact path="/vehicle-request-remove-replace">
        {autoDetectCurrentType ? <VehicleRemoveReplace/> : <Redirect to="/" />}
      </Route>
      <Route exact path="/change-request">
        {currentForm ? <InsuranceChangeRequest/> : <Redirect to="/" />}
      </Route>
      <Route exact path="/remove-replace-driver">
        {autoDetectCurrentType ? <Remove_Replace_Driver/> : <Redirect to="/" />}
      </Route>
      <Route exact path="/vehicle-request-add">
        {autoDetectCurrentType ? <AddVehicleRequestChange/> : <Redirect to="/" />}
      </Route>
    </Switch>
    </Suspense>
    </>
  )
};
